import {
  Easing,
  EasingFunction,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated'

type PhaseNumbers = readonly number[]
type PhaseEasings = readonly EasingFunction[]

export interface AnimationComponentProps {
  size?: number
  color?: string
  durations?: PhaseNumbers
  delays?: PhaseNumbers
  easings?: PhaseEasings
  /**
   * Whether the looping animation should be running. Pass `false` to
   * cancel it (e.g. while the icon is hidden/inactive) so it isn't
   * wasting work off-screen. Callers should remount (or reset shared
   * values) on reactivation so the loop starts from its initial frame
   * instead of resuming mid-animation. Defaults to `true`.
   */
  isActive?: boolean
}

/**
 * All looping icon animations in this folder share the same strict
 * timeline: 4 phases, each with its own duration/delay/easing. What
 * changes per animation (grow, shrink, rotate, move, ...) is only the
 * target value for each phase, e.g. a size, an opacity, an angle, or an
 * offset - any numeric transform can reuse this engine.
 */
export const PHASE_COUNT = 4

export const DEFAULT_PHASE_DURATIONS: PhaseNumbers = [600, 1200, 800, 200]
export const DEFAULT_PHASE_DELAYS: PhaseNumbers = [0, 0, 0, 400]
export const DEFAULT_PHASE_EASINGS: PhaseEasings = [
  Easing.out(Easing.ease),
  Easing.out(Easing.ease),
  Easing.out(Easing.ease),
  Easing.linear,
]

export const buildPhaseLoop = (
  values: PhaseNumbers,
  durations: PhaseNumbers = DEFAULT_PHASE_DURATIONS,
  delays: PhaseNumbers = DEFAULT_PHASE_DELAYS,
  easings: PhaseEasings = DEFAULT_PHASE_EASINGS,
  /**
   * When `true`, the loop plays the phases forward then backward (a
   * "yoyo") instead of jumping from the last phase straight back to the
   * first one on every repeat.
   */
  reverse = false
) =>
  withRepeat(
    withSequence(
      ...values.map((value, index) =>
        withDelay(
          delays[index],
          withTiming(value, {
            duration: durations[index],
            easing: easings[index],
          })
        )
      )
    ),
    -1,
    reverse
  )
